import { Box, Flex, Text } from '@theme-ui/components';
import { Cultivars } from '../../types/Cultivars';
import { DEFAULT_STEP_OPTIONS } from '../../constants/onboarding';
import { Icon, InlineIcon } from '@iconify/react';
import {
  IconCedroAustraliano,
  IconEucalipto,
  IconMognoAfricano,
  IconPinus,
  IconTeca,
  IconType,
} from '../Icons';
import { LatLng, useGeocoding } from '../MapWithSearch/useGeocoding';
import { PlantDetailsCardCloneDetails } from '../PlantDetailsCardCloneDetails/PlantDetailsCardCloneDetails';
import { Steps } from 'intro.js-react';
import HorizontalLine from '../HorizontalLine/HorizontalLine';
import React, { FC } from 'react';
import ReactModal from 'react-modal';
import ReactTooltip from 'react-tooltip';
import SpecieBadge from '../SpecieBadge/SpecieBadge';
import chevronUpIcon from '@iconify/icons-bx/bx-chevron-up';
import minusIcon from '@iconify/icons-akar-icons/minus';

export type SpecieName =
  | 'Eucalipto'
  | 'Pinus'
  | 'Cedro Australiano'
  | 'Mogno Africano'
  | 'Teca';

export type SpecieType = {
  name: SpecieName;
  selected?: boolean;
};

type Specie = {
  [specieName in SpecieName]: {
    color:
      | 'cedroAustraliano'
      | 'eucalipto'
      | 'mognoAfricano'
      | 'pinus'
      | 'teca';
    name: SpecieName;
    icon: FC<IconType>;
  };
};

export const SPECIE_ATTRIBUTES: Specie = {
  'Cedro Australiano': {
    color: 'cedroAustraliano',
    name: 'Cedro Australiano',
    icon: IconCedroAustraliano,
  },
  Eucalipto: {
    color: 'eucalipto',
    name: 'Eucalipto',
    icon: IconEucalipto,
  },
  'Mogno Africano': {
    color: 'mognoAfricano',
    name: 'Mogno Africano',
    icon: IconMognoAfricano,
  },
  Pinus: {
    color: 'pinus',
    name: 'Pinus',
    icon: IconPinus,
  },
  Teca: {
    color: 'teca',
    name: 'Teca',
    icon: IconTeca,
  },
};

// const clonesDetails: {
//   [key: string]: {
//     specie: SpecieName;
//     rnc: string;
//     snpc: string;
//   };
// } = {
//   AAC: {
//     specie: 'Cedro Australiano',
//     rnc: 'https://google.com',
//     snpc: 'https://google.com',
//   },
// };

type SpecieItemProps = {
  specie: SpecieName;
  selected?: boolean;
  onClick: () => void;
};

const SpecieItem = ({ specie, selected, onClick }: SpecieItemProps) => {
  const Icon = SPECIE_ATTRIBUTES[specie]?.icon;
  const backgroundColor = selected ? SPECIE_ATTRIBUTES[specie]?.color : 'white';
  const name = SPECIE_ATTRIBUTES[specie]?.name;
  const colorIcon = selected ? 'white' : 'black';

  return (
    <Flex
      sx={{
        flexDirection: 'column',
        textAlign: 'center',
        maxWidth: '56px',
        alignItems: 'center',
        cursor: 'pointer',
      }}
      role="button"
      onClick={onClick}
      aria-label="specie-button"
    >
      <Flex
        aria-label={`specie-button--${specie}`}
        sx={{
          backgroundColor,
          width: '56px',
          height: '56px',
          borderRadius: '100px',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          boxShadow: 1,
        }}
      >
        <Icon color={colorIcon} />
      </Flex>
      <Text
        sx={{
          textAlign: 'center',
          flexWrap: 'wrap',
          fontSize: 2,
          marginTop: 4,
          fontFamily: 'body',
          color: 'text',
          fontWeight: 'body',
          lineHeight: 'body',
        }}
      >
        {name}
      </Text>
    </Flex>
  );
};

type PlantDetailsCardFooterProps = {
  currentLocation?: LatLng;
};

const PlantDetailsCardFooter = ({
  currentLocation,
}: PlantDetailsCardFooterProps) => {
  const [isOnTop, setIsOnTop] = React.useState(true);
  const [y, setY] = React.useState(window.scrollY);

  const [address, setAddress] = React.useState('');
  const latLng = React.useMemo(() => {
    if (!currentLocation) {
      return null;
    }

    const { lat, lng } = currentLocation;

    return `${lat}, ${lng}`;
  }, [currentLocation]);

  const { getAddress } = useGeocoding();

  React.useEffect(() => {
    if (currentLocation) {
      getAddress(currentLocation, true).then((res: any) => {
        // eslint-disable-next-line no-unsafe-optional-chaining
        const [result] = res?.filter?.((item: any) => {
          return (
            item?.types?.includes?.('postal_code') ||
            item?.types?.includes?.('administrative_area_level_1') ||
            item?.types?.includes?.('administrative_area_level_2') ||
            item?.types?.includes?.('political')
          );
        });
        setAddress(
          String(result?.formatted_address).replace(
            // eslint-disable-next-line no-useless-escape
            /\d\d\d\d\d\-\d\d\d\,/gi,
            ''
          )
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocation]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleNavigation = (e: any) => {
    const window = e.currentTarget;

    if (window.scrollY < 100) {
      setIsOnTop(true);
    } else {
      setIsOnTop(false);
    }

    setY(window.scrollY);
  };

  React.useEffect(() => {
    window.addEventListener('scroll', (e) => {
      return handleNavigation(e);
    });

    return () => {
      window.removeEventListener('scroll', (e) => {
        return handleNavigation(e);
      });
    };
  }, [y]);

  const scrollDown = () => {
    window.scrollTo(0, 200);
  };

  const scrollUp = () => {
    window.scrollTo(0, 0);
  };

  const handleSearch = () => {
    scrollUp();
    const inputEl = document.getElementById('input-search-autocomplete');

    if (inputEl) {
      inputEl.focus();
    }
  };

  return (
    <Flex
      role="button"
      sx={{
        paddingX: 7,
        alignItems: 'center',
        backgroundColor: 'secondary',
        fontFamily: 'body',
        position: 'fixed',
        bottom: isOnTop ? 0 : 'initial',
        top: isOnTop ? 'initial' : 0,
        left: 0,
        right: 0,
        display: !isOnTop && (!address || !latLng) ? 'none' : ['flex', 'none'],
        scrollBehavior: 'smooth',
        zIndex: 1,
      }}
      onClick={isOnTop ? scrollDown : undefined}
    >
      {isOnTop ? (
        <>
          <Text sx={{ fontSize: 4 }}>
            <InlineIcon color="white" icon={chevronUpIcon} />
          </Text>

          <Text sx={{ marginLeft: 7, color: 'white', fontSize: 2 }}>
            Clique aqui para ver mais
          </Text>
        </>
      ) : (
        <Flex
          sx={{
            flexDirection: 'column',
            width: '100%',
            paddingBottom: 5,
          }}
        >
          <Text
            sx={{
              fontSize: 3,
              color: 'white',
              textAlign: 'center',
            }}
          >
            <Icon icon="akar-icons:minus" />
          </Text>

          <Flex
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 6,
            }}
          >
            <Text sx={{ fontSize: 3, color: 'white' }} onClick={scrollUp}>
              <Icon icon="ic:round-arrow-back" />
            </Text>

            {/* TODO: This part is waiting for the save page */}
            {/* <Text as="ins" sx={{ fontSize: 2, color: 'white' }}>
              Clique aqui para salvar seus locais
            </Text> */}

            <Text sx={{ textAlign: 'center', fontSize: 3, color: 'white' }}>
              {address}
            </Text>

            <Flex sx={{ gap: 6 }}>
              <Text
                role="button"
                onClick={handleSearch}
                sx={{ fontSize: 3, color: 'white' }}
              >
                <Icon icon="ic:round-search" />
              </Text>

              {/* TODO: This functionality is waiting for rule's definition */}
              {/* <Text sx={{ fontSize: 3, color: 'white' }}>
                <Icon icon="ic:outline-share" />
              </Text> */}
            </Flex>
          </Flex>

          {latLng && (
            <Text
              sx={{
                textAlign: 'center',
                fontSize: 3,
                color: 'white',
                marginTop: 4,
              }}
            >
              {latLng}
            </Text>
          )}
        </Flex>
      )}
    </Flex>
  );
};

export type PlantDetailsCardProps = {
  currentLocation?: LatLng;
  species: SpecieType[];
  recommendations?: {
    label: string;
    description?: string;
    cultivars: Cultivars[];
  }[];
};

export const ONBOARD_KEY = 'onboarding:PlantDetailsCard:Modal';

const PlantDetailsCard = ({
  species,
  recommendations = [],
  currentLocation,
}: PlantDetailsCardProps) => {
  const [selectedClone, setSelectedClone] = React.useState<Cultivars>();

  const [selectedSpecies, setSelectedSpecies] = React.useState<string[]>(
    species.map((item) => {
      return item.name;
    })
  );

  const handleSelectSpecie = (specie: string) => {
    setSelectedSpecies((prevState) => {
      const specieFound = prevState.find((s) => {
        return s === specie;
      });

      if (specieFound) {
        return prevState.filter((s) => {
          return s !== specie;
        });
      }

      return prevState.concat(specie);
    });
  };

  const closeModal = () => {
    setSelectedClone(undefined);
  };

  const [state, setState] = React.useState({
    stepsEnabled: false,
    initialStep: 0,
    steps: [
      {
        element: '.clone-link',
        intro:
          'Clicando aqui você também pode acessar informações da espécie/clone selecionada que estão registradas no mapa',
      },
    ],
  });

  React.useEffect(() => {
    const showOnboarding = !localStorage.getItem(ONBOARD_KEY);

    if (showOnboarding && !!selectedClone) {
      setTimeout(() => {
        setState((prev) => {
          return { ...prev, stepsEnabled: true };
        });
      }, 1000);
    }
  }, [selectedClone]);

  const onExit = () => {
    setState((prev) => {
      return { ...prev, stepsEnabled: false };
    });

    localStorage.setItem(ONBOARD_KEY, 'showed');
  };

  return (
    <Box>
      <ReactModal
        isOpen={!!selectedClone}
        style={{
          content: {
            position: 'absolute',
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
          },
        }}
      >
        {selectedClone && (
          <PlantDetailsCardCloneDetails
            {...selectedClone}
            goBack={closeModal}
          />
        )}
      </ReactModal>

      <Steps
        enabled={state.stepsEnabled}
        steps={state.steps}
        initialStep={state.initialStep}
        onExit={onExit}
        options={DEFAULT_STEP_OPTIONS}
      />

      <Flex sx={{ justifyContent: 'center' }}>
        <Text sx={{ textAlign: 'center', fontSize: 4, color: 'muted' }}>
          <InlineIcon icon={minusIcon} />
        </Text>
      </Flex>

      <Box sx={{ padding: 6 }}>
        <Box sx={{ marginBottom: 6 }} className="select-species">
          <Text
            sx={{
              fontFamily: 'heading',
              fontSize: 2,
              color: 'dark',
              fontWeight: 'heading',
            }}
          >
            Selecione as espécies
          </Text>

          <Flex
            sx={{
              marginTop: 5,
              justifyContent: 'space-between',
              maxWidth: '480px',
              margin: '8px auto 0',
            }}
          >
            {species?.map((specie) => {
              return (
                <SpecieItem
                  key={specie.name}
                  selected={
                    !!selectedSpecies.find((s) => {
                      return s === specie.name;
                    })
                  }
                  specie={specie.name}
                  onClick={() => {
                    return handleSelectSpecie(specie.name);
                  }}
                />
              );
            })}
          </Flex>
        </Box>

        <HorizontalLine />

        <Box sx={{ marginTop: 5 }}>
          {selectedSpecies.length === 0 && (
            <Flex
              sx={{
                justifyContent: 'center',
                alignItems: 'center',
                paddingY: 6,
              }}
            >
              <Text sx={{ fontSize: [2, 3], color: 'neutral' }}>
                Selecione uma espécie acima para exibir as recomendações
              </Text>
            </Flex>
          )}

          {selectedSpecies.length > 0 &&
            recommendations?.map(({ label, cultivars = [], description }) => {
              const filteredCultivars = cultivars.filter((item) => {
                return selectedSpecies.includes(item.species);
              });

              return (
                <Box
                  sx={{
                    marginTop: 6,
                    '.tipOverrides': {
                      maxWidth: '100%',
                      backgroundColor: 'white',
                      color: 'text',
                    },
                  }}
                  key={label}
                >
                  <ReactTooltip
                    multiline={true}
                    scrollHide={true}
                    class="tipOverrides"
                    effect="solid"
                    place="right"
                    type="info"
                  />
                  <Text
                    data-tip={description}
                    sx={{
                      fontFamily: 'heading',
                      fontWeight: 'heading',
                      fontSize: 2,
                      color: 'dark',
                    }}
                    className={`specie-${label
                      .toLowerCase()
                      .split(' ')
                      .join('-')}`}
                  >
                    {label} <InlineIcon icon="akar-icons:info-fill" />
                  </Text>

                  {filteredCultivars.length > 0 ? (
                    <Flex
                      sx={{
                        gap: 4,
                        whiteSpace: 'nowrap',
                        margin: '8px auto 0',
                        flexWrap: 'wrap',
                      }}
                    >
                      {filteredCultivars.map((recommendation, cultivarsIdx) => {
                        return (
                          <Box
                            key={`${recommendation.code}`}
                            sx={{
                              display: 'inline-block',
                              minWidth: 'unset',
                              cursor: 'pointer',
                            }}
                            onClick={() => {
                              return setSelectedClone(recommendation);
                            }}
                            className={
                              cultivarsIdx === 0
                                ? 'first-specie-item-badge'
                                : undefined
                            }
                          >
                            <SpecieBadge {...recommendation} />
                          </Box>
                        );
                      })}
                    </Flex>
                  ) : (
                    <Flex
                      sx={{
                        justifyContent: 'center',
                        alignItems: 'center',
                        paddingTop: 6,
                      }}
                    >
                      <Text sx={{ fontSize: [2, 3], color: 'neutral' }}>
                        Não há recomendações nesta categoria
                      </Text>
                    </Flex>
                  )}

                  <HorizontalLine sx={{ marginTop: 7 }} />
                </Box>
              );
            })}
        </Box>
      </Box>

      <PlantDetailsCardFooter currentLocation={currentLocation} />
    </Box>
  );
};

export default PlantDetailsCard;
